import { TUNABLES } from "../config";
import type { SeatId } from "../core";
import { createMatch, type Match, type RoomLink, type RoomMatch, stateOfSnapshot } from "./match";
import {
  type PublicGame,
  parseLobbyServer,
  parseRoomServer,
  type RejectReason,
  type RoomClient,
  type RoomMode,
  type SeatedPlayer,
} from "./protocol";
import { backoffMs, type LinkClock, type SocketLike, wireSocket } from "./socketLink";

/**
 * The page's end of the lobby server (B3.5): the lobby's list read against the page's own clock,
 * and the link to one room. Up to the start the link seats the page; from it the room's turns,
 * flags and pongs go to the match (`match.ts`, B3.6), which holds them until the game, still
 * loading, takes it. Since B3.7 the link outlives its socket: a socket that closes, or says
 * nothing for `silentMs`, is replaced, the page joins again with the last turn it took and the
 * room sends the rest (D-026); after `giveUpMs` without the room the match is lost and the game
 * plays on alone (the kill switch). Browser code with the sockets and the clock given to it
 * (`socketLink.ts`, re-exported here for the page and the tests), so the tests drive it with fakes.
 * K4: a page that comes in under way starts from the snapshot the start carries (one that does not
 * hash is refused), and the room's offers, resyncs and tables after the start go to the match.
 * K4.1: a seated page may ask the room to start now (`ready`), and hears how many asked.
 */
export type { LinkClock, SocketLike } from "./socketLink";

/** A lobby's list on the page's clock: `startsAt` moved by how far the server's clock is ahead. */
export function gamesOnPageClock(
  raw: string,
  receivedAt: number,
): readonly PublicGame[] | undefined {
  const message = parseLobbyServer(raw);
  if (!message) {
    return undefined;
  }
  const ahead = message.serverTime - receivedAt;
  return message.games.map((game) =>
    game.startsAt === null ? game : { ...game, startsAt: game.startsAt - ahead },
  );
}

export interface RoomEvents {
  /**
   * The room gave this page a seat at its table, in its mode (K2); `ready`, how many seated humans
   * asked to start now (K4.1), undefined from a room that takes no ask.
   */
  seated(
    seat: SeatId,
    table: readonly SeatedPlayer[],
    mode: RoomMode,
    ready: number | undefined,
  ): void;
  /** Someone sat, left or asked to start now before the start. */
  table(table: readonly SeatedPlayer[], ready: number | undefined): void;
  /**
   * The match starts on its seed in the room's mode (K2); `room.held` are the seats humans held at
   * its start, the rest go to scripts. The match already holds every turn that comes from here on (B3.6).
   */
  start(seat: SeatId, seedText: string, room: RoomMatch): void;
  rejected(reason: RejectReason): void;
  /** The room could not be reached before the start. */
  lost(): void;
}

export function joinRoom(options: {
  /** A new socket to the room, for the join and for every rejoin. */
  readonly open: () => SocketLike;
  readonly join: Extract<RoomClient, { type: "join" }>;
  readonly events: RoomEvents;
  readonly clock: LinkClock;
}): RoomLink {
  const { events, clock } = options;
  let socket: SocketLike | undefined;
  let seat: SeatId | undefined;
  /** K3: the table the room last told this page, the names the match keeps. */
  let table: readonly SeatedPlayer[] = [];
  let match: Match | undefined;
  /** Before the start: the room said its last word to this page, a start or a refusal. */
  let settled = false;
  /** After the start: the page left on purpose, or gave the room up. */
  let down = false;
  let dropMs: number | undefined;
  let attempt = 0;
  let lastHeardMs = clock.now();
  let connectedAtMs = clock.now();
  let watching = false;

  /**
   * The socket that opened, the only one written to: a browser's WebSocket throws on a send while
   * it connects, and a ping, a hash or a leave due then is lost as on a dropped socket.
   */
  let openSocket: SocketLike | undefined;
  const send = (message: RoomClient) => {
    if (socket !== undefined && socket === openSocket) {
      socket.send(JSON.stringify(message));
    }
  };

  const lost = () => {
    if (!settled) {
      settled = true;
      events.lost();
    }
  };

  /** The room is gone for good: the match is played on alone. */
  const giveUp = () => {
    if (!down) {
      down = true;
      socket?.close();
      socket = undefined;
      match?.link("lost");
    }
  };

  /** A started page lost its socket: try again, after a wait, until the room answers or `giveUpMs`. */
  const dropped = (from: SocketLike) => {
    if (from !== socket || down) {
      return;
    }
    socket = undefined;
    if (!match) {
      lost();
      return;
    }
    const now = clock.now();
    if (dropMs === undefined) {
      dropMs = now;
      match.link("dropped");
    }
    if (now - dropMs >= TUNABLES.net.giveUpMs) {
      giveUp();
      return;
    }
    const wait = backoffMs(TUNABLES.net.retryMs, attempt);
    attempt += 1;
    clock.after(wait, () => {
      if (!down && socket === undefined) {
        connect();
      }
    });
  };

  /** One message of the room's on this socket; a replaced socket is not heard. */
  const heard = (current: SocketLike, text: string | undefined) => {
    if (current !== socket) {
      return;
    }
    lastHeardMs = clock.now();
    const message = text === undefined ? undefined : parseRoomServer(text);
    switch (message?.type) {
      case "seated":
        seat = message.seat;
        table = message.table;
        if (match) {
          // Back at the table after a drop: the missed turns follow in the next message.
          dropMs = undefined;
          attempt = 0;
          match.link("back");
        } else {
          events.seated(message.seat, message.table, message.mode, message.ready);
        }
        return;
      case "table":
        table = message.table;
        if (match) {
          match.link("table");
        } else {
          events.table(message.table, message.ready);
        }
        return;
      case "start": {
        if (seat === undefined || settled) {
          return;
        }
        settled = true;
        const snapshot = message.snapshot && stateOfSnapshot(message.snapshot);
        if (message.snapshot && !snapshot) {
          events.rejected("shape");
          current.close();
          return;
        }
        match = createMatch({ seat, send, now: clock.now });
        events.start(seat, message.seedText, {
          match,
          mode: message.mode,
          held: message.held,
          get table() {
            return table;
          },
          link,
          snapshot,
        });
        return;
      }
      case "offer":
        match?.offer(message.tick);
        return;
      case "resync":
        match?.resync(message.snapshot);
        return;
      case "turns":
        match?.hold(message.from, message.turns);
        return;
      case "desync":
        match?.desync(message.tick);
        return;
      case "pong":
        match?.pong(message.sentAt);
        return;
      case "rejected":
        if (seat === undefined && !match) {
          settled = true;
          events.rejected(message.reason);
          current.close();
        } else if (match && dropMs !== undefined) {
          // The room no longer knows this page (it restarted): there is nothing to rejoin.
          giveUp();
        } else {
          match?.refused(message.reason);
        }
        return;
      default:
        return;
    }
  };

  const connect = () => {
    const current = options.open();
    socket = current;
    lastHeardMs = clock.now();
    connectedAtMs = lastHeardMs;
    wireSocket(current, {
      open: () => {
        openSocket = current;
        const last = match?.lastTaken();
        send(last === undefined ? options.join : { ...options.join, lastTurn: last });
      },
      message: (text) => heard(current, text),
      gone: () => dropped(current),
    });
  };

  /**
   * After the start a socket that says nothing for `silentMs` is as good as dropped (the room sends
   * a turn every tick); before it, the room is quiet through the countdown, and only a seat that
   * never comes (`connectTimeoutMs`) counts as the room unreachable.
   */
  const watch = () => {
    if (down || (settled && !match)) {
      watching = false;
      return;
    }
    const now = clock.now();
    if (match && socket && now - lastHeardMs > TUNABLES.net.silentMs) {
      const silent = socket;
      silent.close();
      dropped(silent);
    } else if (
      !match &&
      seat === undefined &&
      socket &&
      now - connectedAtMs > TUNABLES.lobby.connectTimeoutMs
    ) {
      socket.close();
      lost();
    }
    clock.after(TUNABLES.net.silentMs / 2, watch);
  };
  const startWatching = () => {
    if (!watching) {
      watching = true;
      clock.after(TUNABLES.net.silentMs / 2, watch);
    }
  };

  const link: RoomLink = {
    ready() {
      if (seat !== undefined && !settled && !match) {
        send({ type: "ready" });
      }
    },
    leave() {
      // A leave while the socket is down or still connecting stays a leave: no try comes back,
      // and the room hands the seat over when its grace runs out.
      if (match && !down) {
        send({ type: "leave" });
        down = true;
        const leaving = socket;
        socket = undefined;
        leaving?.close();
      }
    },
    rejoin() {
      if (match && down && socket === undefined) {
        down = false;
        dropMs = undefined;
        attempt = 0;
        connect();
        startWatching();
      }
    },
    drop() {
      if (socket) {
        const cut = socket;
        cut.close();
        dropped(cut);
      }
    },
  };
  connect();
  startWatching();
  return link;
}
